/**
 * 購読（チャットの返答のストリーム）を、WebSocket と SSE のどちらで受けるかを決める。
 * 既定は WebSocket。環境変数で指定した組織だけ SSE（新しい WebSocket 接続を作らずに済む）で受け、
 * ブラウザごとの上書きもできる。組織の一覧を変えるには、画面の再デプロイが要る。
 */

export type SubscriptionTransport = 'ws' | 'sse';

export const SUBSCRIPTION_TRANSPORT_STORAGE_KEY = 'kuden:subscription-transport';
export const SUBSCRIPTION_TRANSPORT_URL_PARAM = 'subscriptionTransport';
// 値が `default` のとき、ブラウザごとの上書きを消す
const OVERRIDE_RESET_VALUE = 'default';
const ALL_ORGANIZATIONS = '*';

type ResolveInput = {
  organizationId: string | null;
  sseOrganizationIds: string[];
  override: string | null;
};

let currentOrganizationId: string | null = null;

export function parseOrganizationIds(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map(id => id.trim())
    .filter(id => id !== '');
}

const isTransport = (value: string | null): value is SubscriptionTransport => value === 'ws' || value === 'sse';

export function resolveSubscriptionTransport({
  organizationId,
  sseOrganizationIds,
  override,
}: ResolveInput): SubscriptionTransport {
  if (isTransport(override)) {
    return override;
  }
  const isSseOrganization =
    sseOrganizationIds.includes(ALL_ORGANIZATIONS) ||
    (organizationId !== null && sseOrganizationIds.includes(organizationId));
  return isSseOrganization ? 'sse' : 'ws';
}

export function setSubscriptionOrganizationId(organizationId: string | null): void {
  currentOrganizationId = organizationId;
}

const readOverride = (): string | null => {
  try {
    return window.localStorage.getItem(SUBSCRIPTION_TRANSPORT_STORAGE_KEY);
  } catch {
    // ストレージが使えない環境（プライベートブラウズなど）では、上書きなしとして扱う
    return null;
  }
};

/**
 * URL の `subscriptionTransport=sse|ws|default` を、ブラウザごとの上書きとして保存する。
 * 実機（iPhone の Safari など）で、組織の設定に関係なく試すための入口。
 */
export function captureSubscriptionTransportOverride(search: string): void {
  const requested = new URLSearchParams(search).get(SUBSCRIPTION_TRANSPORT_URL_PARAM);
  try {
    if (requested === OVERRIDE_RESET_VALUE) {
      window.localStorage.removeItem(SUBSCRIPTION_TRANSPORT_STORAGE_KEY);
    } else if (isTransport(requested)) {
      window.localStorage.setItem(SUBSCRIPTION_TRANSPORT_STORAGE_KEY, requested);
    }
  } catch {
    // ストレージが使えない環境では、上書きを保存できないだけで、通信には影響しない
  }
}

export function getSubscriptionTransport(): SubscriptionTransport {
  return resolveSubscriptionTransport({
    organizationId: currentOrganizationId,
    sseOrganizationIds: parseOrganizationIds(process.env.NEXT_PUBLIC_SSE_SUBSCRIPTION_ORGANIZATION_IDS),
    override: readOverride(),
  });
}
